import React, { useState, useEffect, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import SearchIcon from '@material-ui/icons/Search';
import AssessmentIcon from '@material-ui/icons/Assessment';
import SendIcon from '@material-ui/icons/Send';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import BarChartIcon from '@material-ui/icons/BarChart';
import PeopleIcon from '@material-ui/icons/People';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import VisibilityIcon from '@material-ui/icons/Visibility';
import FileCopyIcon from '@material-ui/icons/FileCopy';

import { DataPulseContext } from '../contexts/DataPulseContext';
import { ScreenContext } from '../contexts/ScreenContext';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.grey[50],
    minHeight: '100vh',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  statsContainer: {
    marginBottom: theme.spacing(4),
  },
  statCard: {
    textAlign: 'center',
    padding: theme.spacing(2),
    height: '100%',
  },
  statNumber: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  statLabel: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
  },
  searchField: {
    marginBottom: theme.spacing(3),
    backgroundColor: 'white',
  },
  surveyCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'all 0.2s ease',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[8],
    },
  },
  surveyName: {
    fontWeight: 600,
    fontSize: '1.1rem',
    marginBottom: theme.spacing(1),
  },
  surveyDescription: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
    minHeight: '3em',
  },
  surveyStats: {
    display: 'flex',
    justifyContent: 'space-around',
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  statItem: {
    textAlign: 'center',
  },
  statValue: {
    fontWeight: 'bold',
    fontSize: '1.2rem',
    color: theme.palette.primary.main,
  },
  statText: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  statusChip: {
    fontWeight: 500,
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(6),
  },
  emptyIcon: {
    fontSize: '4rem',
    color: theme.palette.grey[400],
    marginBottom: theme.spacing(2),
  },
  cardActions: {
    justifyContent: 'flex-end',
    padding: theme.spacing(1),
  },
}));

const DataPulse = () => {
  const classes = useStyles();
  const history = useHistory();
  const { surveys, isLoading, loadSurveys, duplicateSurvey, deleteSurvey, updateSurvey, fetchSurveyPreviewHtml, getSurveyCampaigns } = useContext(DataPulseContext);
  const { setSnackbarMessage } = useContext(ScreenContext);

  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, survey: null, campaigns: [] });
  const [previewDialog, setPreviewDialog] = useState({ open: false, surveyId: null });
  const [previewHtml, setPreviewHtml] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedSurvey, setSelectedSurvey] = useState(null);

  useEffect(() => {
    loadSurveys();
  }, []);

  // Filter surveys
  const filteredSurveys = surveys.filter(survey =>
    survey.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (survey.description && survey.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Calculate overall stats
  const totalSurveys = surveys.length;
  const totalResponses = surveys.reduce((sum, s) => sum + (parseInt(s.completed_count) || 0), 0);
  const totalSent = surveys.reduce((sum, s) => sum + (parseInt(s.total_recipients) || 0), 0);
  const avgCompletionRate = surveys.length > 0
    ? (surveys.reduce((sum, s) => sum + (parseFloat(s.completion_rate) || 0), 0) / surveys.length).toFixed(1)
    : 0;

  const handleMenuOpen = (event, survey) => {
    setAnchorEl(event.currentTarget);
    setSelectedSurvey(survey);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedSurvey(null);
  };

  const handleCreateSurvey = () => {
    history.push('/data-pulse/create');
  };

  const handleEditSurvey = () => {
    history.push(`/data-pulse/edit/${selectedSurvey.id}`);
    handleMenuClose();
  };

  const handleViewAnalytics = () => {
    history.push(`/data-pulse/analytics/${selectedSurvey.id}`);
    handleMenuClose();
  };

  const handleDuplicateSurvey = async () => {
    const surveyToDupe = selectedSurvey;
    handleMenuClose();
    try {
      const result = await duplicateSurvey(surveyToDupe.id);
      setSnackbarMessage({ 
        message: `Survey duplicated: "${result.name}" (${result.questions_copied} questions copied)`, 
        severity: 'success' 
      });
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to duplicate survey';
      setSnackbarMessage({ message: errorMsg, severity: 'error' });
    }
  };

  const handlePreviewClick = async (surveyId) => {
    setPreviewDialog({ open: true, surveyId });
    setPreviewHtml(''); // Clear previous preview
    try {
      const html = await fetchSurveyPreviewHtml(surveyId);
      setPreviewHtml(html);
    } catch (error) {
      console.error('Error loading preview:', error);
      setSnackbarMessage({ message: 'Failed to load preview', severity: 'error' });
    }
  };

  const handleDeleteClick = async () => {
    handleMenuClose();
    // Fetch associated campaigns first
    try {
      const campaignData = await getSurveyCampaigns(selectedSurvey.id);
      setDeleteDialog({ open: true, survey: selectedSurvey, campaigns: campaignData.campaigns || [] });
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      setDeleteDialog({ open: true, survey: selectedSurvey, campaigns: [] });
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const result = await deleteSurvey(deleteDialog.survey.id);
      const campaignCount = result.campaigns_deleted || 0;
      const message = campaignCount > 0 
        ? `Survey and ${campaignCount} associated campaign(s) deleted successfully`
        : 'Survey deleted successfully';
      setSnackbarMessage({ message, severity: 'success' });
      setDeleteDialog({ open: false, survey: null, campaigns: [] });
    } catch (error) {
      setSnackbarMessage({ message: 'Failed to delete survey', severity: 'error' });
    }
  };

  const handleStatusChange = async (surveyId, newStatus) => {
    try {
      // Get current survey data
      const survey = surveys.find(s => s.id === surveyId);
      await updateSurvey(surveyId, { ...survey, status: newStatus });
      setSnackbarMessage({ message: `Survey ${newStatus}`, severity: 'success' });
    } catch (error) {
      setSnackbarMessage({ message: 'Failed to update survey status', severity: 'error' });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'primary';
      case 'completed': return 'default';
      case 'paused': return 'secondary';
      case 'draft': return 'default';
      default: return 'default';
    }
  };

  if (isLoading && surveys.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className={classes.root}>
      {/* Header */}
      <Box className={classes.header}>
        <Box display="flex" alignItems="center">
          <AssessmentIcon style={{ fontSize: '2rem', marginRight: 12, color: '#b71c1c' }} />
          <Typography variant="h4">
            DataPulse
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleCreateSurvey}
        >
          Create Survey
        </Button>
      </Box>

      {/* Info Box - How to Send Surveys */}
      <Paper style={{ padding: 16, marginBottom: 24, backgroundColor: '#e3f2fd', border: '1px solid #90caf9' }}>
        <Box display="flex" alignItems="center">
          <SendIcon style={{ marginRight: 12, color: '#1976d2' }} />
          <Box>
            <Typography variant="body2" style={{ fontWeight: 600, color: '#1565c0', marginBottom: 4 }}>
              How to Send Surveys
            </Typography>
            <Typography variant="body2" color="textSecondary">
              To send a survey to recipients, go to <strong>Email Campaigns → Create Campaign</strong> and select "Survey Campaign" type.
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={3} className={classes.statsContainer}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper className={classes.statCard} elevation={2}>
            <Typography className={classes.statNumber}>{totalSurveys}</Typography>
            <Typography className={classes.statLabel}>Total Surveys</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper className={classes.statCard} elevation={2}>
            <Typography className={classes.statNumber}>{totalSent}</Typography>
            <Typography className={classes.statLabel}>Surveys Sent</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper className={classes.statCard} elevation={2}>
            <Typography className={classes.statNumber}>{totalResponses}</Typography>
            <Typography className={classes.statLabel}>Total Responses</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper className={classes.statCard} elevation={2}>
            <Typography className={classes.statNumber}>{avgCompletionRate}%</Typography>
            <Typography className={classes.statLabel}>Avg Completion Rate</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Search */}
      <TextField
        className={classes.searchField}
        fullWidth
        variant="outlined"
        placeholder="Search surveys by name or description..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
        }}
      />

      {/* Surveys Grid */}
      {filteredSurveys.length === 0 ? (
        <Paper className={classes.emptyState} elevation={0}>
          <BarChartIcon className={classes.emptyIcon} />
          <Typography variant="h5" gutterBottom style={{ fontWeight: 500 }}>
            {searchTerm ? 'No surveys found' : 'No surveys yet'}
          </Typography>
          <Typography color="textSecondary" gutterBottom style={{ marginBottom: 24 }}>
            {searchTerm
              ? 'Try a different search term'
              : 'Create your first survey to start collecting customer feedback'}
          </Typography>
          {!searchTerm && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleCreateSurvey}
            >
              Create Survey
            </Button>
          )}
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {filteredSurveys.map((survey) => (
            <Grid item xs={12} sm={6} md={4} key={survey.id}>
              <Card className={classes.surveyCard}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Typography className={classes.surveyName}>
                      {survey.name}
                    </Typography>
                    <Chip
                      label={survey.status}
                      size="small"
                      color={getStatusColor(survey.status)}
                      className={classes.statusChip}
                    />
                  </Box>
                  <Typography className={classes.surveyDescription}>
                    {survey.description || 'No description'}
                  </Typography>
                  
                  <Box className={classes.surveyStats}>
                    <Box className={classes.statItem}>
                      <Typography className={classes.statValue}>
                        {survey.total_recipients || 0}
                      </Typography>
                      <Typography className={classes.statText}>Sent</Typography>
                    </Box>
                    <Box className={classes.statItem}>
                      <Typography className={classes.statValue}>
                        {survey.completed_count || 0}
                      </Typography>
                      <Typography className={classes.statText}>Completed</Typography>
                    </Box>
                    <Box className={classes.statItem}>
                      <Typography className={classes.statValue}>
                        {survey.completion_rate || 0}%
                      </Typography>
                      <Typography className={classes.statText}>Rate</Typography>
                    </Box>
                  </Box>
                  
                  <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 12 }}>
                    Created {new Date(survey.created_at).toLocaleDateString()}
                  </Typography>
                </CardContent>
                
                <CardActions className={classes.cardActions}>
                  <Tooltip title="Preview Survey">
                    <IconButton 
                      size="small" 
                      onClick={() => handlePreviewClick(survey.id)}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="View Analytics">
                    <IconButton 
                      size="small" 
                      onClick={() => history.push(`/data-pulse/analytics/${survey.id}`)}
                    >
                      <BarChartIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="More Actions">
                    <IconButton 
                      size="small" 
                      onClick={(e) => handleMenuOpen(e, survey)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Tooltip>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => { handleMenuClose(); handlePreviewClick(selectedSurvey?.id); }}>
          <VisibilityIcon fontSize="small" style={{ marginRight: 8 }} />
          Preview Survey
        </MenuItem>
        <MenuItem onClick={handleEditSurvey}>
          <EditIcon fontSize="small" style={{ marginRight: 8 }} />
          Edit Survey
        </MenuItem>
        <MenuItem onClick={handleDuplicateSurvey}>
          <FileCopyIcon fontSize="small" style={{ marginRight: 8 }} />
          Duplicate Survey
        </MenuItem>
        <MenuItem onClick={handleViewAnalytics}>
          <BarChartIcon fontSize="small" style={{ marginRight: 8 }} />
          View Analytics
        </MenuItem>
        <MenuItem onClick={handleDeleteClick}>
          <DeleteIcon fontSize="small" style={{ marginRight: 8 }} />
          Delete Survey
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, survey: null, campaigns: [] })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Survey</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Are you sure you want to delete <strong>"{deleteDialog.survey?.name}"</strong>?
          </Typography>
          
          {deleteDialog.campaigns && deleteDialog.campaigns.length > 0 && (
            <Box mt={2} p={2} bgcolor="#fff3cd" borderRadius={1} border="1px solid #ffc107">
              <Typography variant="body2" style={{ fontWeight: 600, color: '#856404', marginBottom: 8 }}>
                ⚠️ Warning: Associated Email Campaigns
              </Typography>
              <Typography variant="body2" style={{ color: '#856404', marginBottom: 8 }}>
                This survey has <strong>{deleteDialog.campaigns.length} email campaign(s)</strong> that will also be deleted:
              </Typography>
              <Box ml={2}>
                {deleteDialog.campaigns.slice(0, 3).map((campaign, index) => (
                  <Typography key={campaign.id} variant="caption" display="block" style={{ color: '#856404' }}>
                    • {campaign.name} ({campaign.status})
                  </Typography>
                ))}
                {deleteDialog.campaigns.length > 3 && (
                  <Typography variant="caption" display="block" style={{ color: '#856404' }}>
                    • ...and {deleteDialog.campaigns.length - 3} more
                  </Typography>
                )}
              </Box>
            </Box>
          )}
          
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
            This will permanently remove all questions, responses, analytics data, and associated campaigns.
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, survey: null, campaigns: [] })}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="secondary" variant="contained">
            Delete Survey{deleteDialog.campaigns && deleteDialog.campaigns.length > 0 ? ' and Campaigns' : ''}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog
        open={previewDialog.open}
        onClose={() => setPreviewDialog({ open: false, surveyId: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Survey Preview
          <Button
            onClick={() => setPreviewDialog({ open: false, surveyId: null })}
            style={{ position: 'absolute', right: 8, top: 8 }}
          >
            Close
          </Button>
        </DialogTitle>

        <DialogContent style={{ padding: 0, overflow: 'hidden' }}>
          {previewDialog.surveyId ? (
            previewHtml ? (
              <iframe
                title="Survey Preview"
                srcDoc={previewHtml}
                sandbox="allow-scripts allow-forms"
                style={{
                  width: '100%',
                  height: '70vh',
                  border: 'none',
                }}
              />
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" height="70vh">
                <CircularProgress />
              </Box>
            )
          ) : null}
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default DataPulse;
